import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, catchError, map } from 'rxjs';
import { getApiEndpoint } from '@app/shared/utils/url.utils';
import { parseApiError } from '@app/shared/models/api/api-error.model';
import { PaymentOrderModel } from '../models/payment-order.model';
import { PaymentModel } from '../models/payment.model';
import { PaymentRequestInfo } from '../models/payment-request-info.model';
import { AkitaAuthQuery } from '../../auth/state/auth.query';
import { PaymentMethod, PaymentProvider } from '../models/payment.provider.model';

@Injectable({ providedIn: 'root' })
export class OrderApiService {
  private readonly http = inject(HttpClient);

  constructor(private readonly akitaAuthQuery: AkitaAuthQuery) {}

  public createOrderApi(
    info: PaymentRequestInfo | null
  ): Observable<PaymentOrderModel | null> {
    // eslint-disable-next-line deprecation/deprecation
    const apiUrl: string = getApiEndpoint('api/v1/order', true);

    if (!info || !info.session_id) {
      return throwError(() => parseApiError('No paymentInfo provided'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .post<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Body
        info,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => PaymentOrderModel.fromJson(data))
      );
  }

  public getOrderApi(orderId: string | null): Observable<PaymentOrderModel | null> {
    const apiUrl: string = getApiEndpoint(`api/v1/order/${orderId}`, true);

    if (!orderId) {
      return throwError(() => parseApiError('No orderId provided'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .get<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => PaymentOrderModel.fromJson(data))
      );
  }

  public getPaymentApi(orderId: string | null): Observable<PaymentModel | null> {
    const apiUrl: string = getApiEndpoint(`api/v2/payment`, true);

    if (!orderId) {
      return throwError(() => parseApiError('No orderId provided'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .get<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
          params: {
            order_id: orderId,
          },
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => PaymentModel.fromJson(data))
      );
  }

  public updatePaymentApi(
    orderId: string,
    info: PaymentRequestInfo | null,
    paymentProvider?: PaymentProvider | null,
    paymentMethod?: PaymentMethod | null,
    discountCode?: string | null
  ): Observable<PaymentModel | null> {
    const apiUrl: string = getApiEndpoint(`api/v2/payment`, true);

    if (!info || !info.session_id || !paymentProvider || !paymentMethod) {
      return throwError(() => parseApiError('No paymentInfo provided'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    const body = {
      ...info,
      payment_provider: `${paymentProvider}`,
      payment_method: `${paymentMethod}`,
      discount_code: discountCode,
    };

    return this.http
      .put<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Body
        body,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
          params: {
            order_id: orderId,
          },
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => PaymentModel.fromJson(data))
      );
  }

  public sendOtpVerificationApi(
    paymentId: string,
    otp: string
  ): Observable<PaymentModel | null> {
    const apiUrl: string = getApiEndpoint(`api/v2/payment/stc/otp`, true);

    if (!paymentId) {
      return throwError(() => parseApiError('No paymentId provided'));
    }

    // eslint-disable-next-line deprecation/deprecation
    const credentials = btoa(`api:GKbwgZ9rJBf8oggS`);
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Basic ${credentials}`,
    });

    const body = {
      otp,
      paymentId,
    };

    return this.http
      .post<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Body
        body,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => PaymentModel.fromJson(data))
      );
  }

  public getUserOrdersApi(): Observable<Array<PaymentOrderModel> | null> {
    const apiUrl: string = getApiEndpoint(`api/v1/order/me`, true);

    // eslint-disable-next-line deprecation/deprecation
    const accessToken = this.akitaAuthQuery.accessToken;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${accessToken}`,
    });

    return this.http
      .get<any>(
        // Assemble the full API URL
        apiUrl,
        // Add the Options
        {
          responseType: 'json',
          headers: headers,
        }
      )
      .pipe(
        catchError((error: unknown) => throwError(() => parseApiError(error))),
        map((data) => PaymentOrderModel.fromJsonList(data.results))
      );
  }
}
